import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { fetchInvoices } from '../models/InvoiceService';
import { useAuth } from '../models/AuthContext';
import {
  CATEGORIES, computePeriod, currentQuarter, openDeclaration, shiftQuarter,
  quarterOf, basisDate, daysUntil, fmtDay, fmtDayShort, hasCashDate,
  URSSAF_URL,
} from '../models/urssaf';
import PageShell, { Btn, Pill, fmtEUR } from '../components/design/PageShell';
import { Ic } from '../components/design/Icons';

const pct = (n) => `${(n * 100).toFixed(2).replace('.', ',').replace(/,00$/, '')} %`;
/** Les montants URSSAF sont des entiers d'euros. */
const eur0 = (n) => Math.round(n).toLocaleString('fr-FR');

/* ------------------------------------------------------- décompte URSSAF */

function ChargesBreakdown({ charges }) {
  const P = CATEGORIES.prestations;
  const M = CATEGORIES.marchandises;
  const hasP = charges.baseP > 0;
  const hasM = charges.baseM > 0;

  return (
    <table className="ta-kv">
      <thead>
        <tr>
          <th>Décompte des cotisations</th>
          <th>Taux</th>
          <th>Montant</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Cotisations sociales</td>
          <td className="rate">—</td>
          <td>{eur0(charges.cotisations)} €</td>
        </tr>
        {hasP && (
          <tr className="sub">
            <td>· prestations de services</td>
            <td className="rate">{pct(P.cotisations)}</td>
            <td>{eur0(charges.cotisationsP)} €</td>
          </tr>
        )}
        {hasM && (
          <tr className="sub">
            <td>· ventes de marchandises</td>
            <td className="rate">{pct(M.cotisations)}</td>
            <td>{eur0(charges.cotisationsM)} €</td>
          </tr>
        )}
        <tr>
          <td>Contribution formation professionnelle</td>
          <td className="rate">{pct(P.cfp)}</td>
          <td>{eur0(charges.cfp)} €</td>
        </tr>
        <tr>
          <td>Taxe pour frais de chambre (CCI/CMA)</td>
          <td className="rate">{pct(P.chambre)} / {pct(M.chambre)}</td>
          <td>{eur0(charges.chambre)} €</td>
        </tr>
        <tr>
          <td>Versement libératoire de l'impôt sur le revenu</td>
          <td className="rate">—</td>
          <td>{eur0(charges.liberatoire)} €</td>
        </tr>
        {hasP && (
          <tr className="sub">
            <td>· prestations de services</td>
            <td className="rate">{pct(P.liberatoire)}</td>
            <td>{eur0(charges.liberatoireP)} €</td>
          </tr>
        )}
        {hasM && (
          <tr className="sub">
            <td>· ventes de marchandises</td>
            <td className="rate">{pct(M.liberatoire)}</td>
            <td>{eur0(charges.liberatoireM)} €</td>
          </tr>
        )}
        <tr className="total">
          <td>Total à payer</td>
          <td />
          <td>{eur0(charges.total)} €</td>
        </tr>
      </tbody>
    </table>
  );
}

/* -------------------------------------------- CA à saisir dans le formulaire */

function RevenueToDeclare({ charges }) {
  return (
    <table className="ta-kv">
      <thead>
        <tr>
          <th>Chiffre d'affaires à déclarer</th>
          <th>Montant</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>BIC prestations de services</td>
          <td>{eur0(charges.baseP)} €</td>
        </tr>
        <tr>
          <td>BIC ventes de marchandises</td>
          <td>{eur0(charges.baseM)} €</td>
        </tr>
        <tr>
          <td>Recettes des activités libérales (BNC)</td>
          <td style={{ color: 'var(--ink-4)' }}>—</td>
        </tr>
        <tr className="total">
          <td>CA total</td>
          <td style={{ color: 'var(--accent)' }}>{eur0(charges.base)} €</td>
        </tr>
      </tbody>
    </table>
  );
}

/* ---------------------------------------------------------------- panneaux */

function PeriodPanel({ quarter, period, tone, badge, footnote, cta }) {
  const c = period.charges;
  const days = daysUntil(quarter.due);
  const urgent = days <= 7;

  return (
    <section className={`ta-panel ${tone === 'accent' ? 'ta-panel-accent' : tone === 'warn' ? 'ta-panel-warn' : ''}`}>
      <div className="ta-panel-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="ta-panel-title">{quarter.label}</span>
            {badge}
          </div>
          <div className="ta-panel-sub">
            {fmtDayShort(quarter.start)} → {fmtDayShort(quarter.end)} · {period.count} facture{period.count > 1 ? 's' : ''}
          </div>
        </div>
        {tone === 'warn' && (
          <div className={`ta-deadline ${urgent ? 'is-urgent' : ''}`}>
            <span>échéance</span>
            <strong>{fmtDay(quarter.due)}</strong>
            <span>
              {days > 1 ? `· ${days} jours restants` : days === 1 ? '· demain' : days === 0 ? '· aujourd’hui' : '· dépassée'}
            </span>
          </div>
        )}
      </div>

      <div className="ta-figs">
        <div>
          <div className="k">{tone === 'warn' ? 'CA encaissé' : 'CA du trimestre'}</div>
          <div className="v">{eur0(c.base)} €</div>
          <div className="h">{eur0(c.baseP)} presta · {eur0(c.baseM)} march.</div>
        </div>
        <div>
          <div className="k">{tone === 'warn' ? 'À payer' : 'Charges estimées'}</div>
          <div className="v" style={{ color: 'var(--warn)' }}>{eur0(c.total)} €</div>
          <div className="h">taux effectif {pct(c.effectiveRate)}</div>
        </div>
        <div>
          <div className="k">Net après charges</div>
          <div className="v" style={{ color: 'var(--accent)' }}>{eur0(c.net)} €</div>
          <div className="h">{eur0(c.net / 3)} € / mois</div>
        </div>
      </div>

      <div className="ta-panel-body">
        <div className="ta-charges-cols">
          <RevenueToDeclare charges={c} />
          <ChargesBreakdown charges={c} />
        </div>
        {footnote}
        {cta && <div style={{ marginTop: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cta}</div>}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- page */

const Charges = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const { currentUser } = useAuth();
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const load = useCallback(async () => {
    try {
      const inv = await fetchInvoices();
      if (!aliveRef.current) return true;
      setInvoices(inv);
      return true;
    } catch (e) {
      console.error('[Charges] chargement:', e);
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const run = async () => {
      const ok = await load();
      if (cancelled || !aliveRef.current) return;
      if (ok) { setLoadError(null); setLoading(false); return; }
      if (attempt < 2) { attempt += 1; setTimeout(() => { if (!cancelled) run(); }, 600 * attempt); return; }
      setLoadError('Impossible de charger les factures.');
      setLoading(false);
    };
    setLoading(true);
    run();
    return () => { cancelled = true; };
  }, [load, currentUser?.uid]);

  const now = new Date();
  const current = currentQuarter(now);
  const toDeclare = openDeclaration(now);

  const currentPeriod = useMemo(
    () => computePeriod(invoices, current.start, current.end),
    [invoices, current.start, current.end]
  );
  const declarePeriod = useMemo(
    () => (toDeclare ? computePeriod(invoices, toDeclare.start, toDeclare.end) : null),
    [invoices, toDeclare]
  );

  /** Historique : trimestres reconstruits depuis le calendrier (fiable),
   *  du plus récent au plus ancien. */
  const history = useMemo(() => {
    if (invoices.length === 0) return [];
    let earliest = null;
    invoices.forEach(inv => {
      const d = basisDate(inv);
      if (d && (!earliest || d < earliest)) earliest = d;
    });
    if (!earliest) return [];

    const first = quarterOf(earliest);
    const out = [];
    let q = current;
    // garde-fou : 40 trimestres = 10 ans
    for (let i = 0; i < 40; i += 1) {
      out.push(q);
      if (q.id === first.id) break;
      q = shiftQuarter(q, -1);
    }
    return out.map(qr => ({ quarter: qr, period: computePeriod(invoices, qr.start, qr.end) }));
  }, [invoices, current]);

  const estimatedCount = (declarePeriod || currentPeriod).estimatedCount;

  const basisNote = (
    <div className="ta-callout" style={{ marginTop: 24, marginBottom: 0 }}>
      <span>
        Le CA est rattaché au trimestre <strong>d'encaissement</strong> (règle URSSAF) dès qu'une date de
        paiement est renseignée sur la facture, sinon à la date de prestation.
        {estimatedCount > 0 && (
          <> {estimatedCount} facture{estimatedCount > 1 ? 's' : ''} de cette période {estimatedCount > 1 ? 'sont rattachées' : 'est rattachée'} à
          leur date de prestation faute de date d'encaissement — le montant reste une estimation.</>
        )}
      </span>
    </div>
  );

  return (
    <PageShell
      section="charges"
      number="05"
      title="Charges"
      subtitle="Cotisations URSSAF · micro-entreprise au versement libératoire"
      actions={
        <a className="ta-btn ta-btn-primary" href={URSSAF_URL} target="_blank" rel="noreferrer">
          <span className="ta-btn-ic">{Ic.arrowR}</span>
          <span>Déclarer sur urssaf.fr</span>
        </a>
      }
    >
      {loading && <div className="mono" style={{ color: 'var(--ink-3)', padding: 24 }}>Chargement…</div>}

      {loadError && (
        <div className="ta-callout ta-callout-warn" style={{ alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{loadError}</span>
          <Btn kind="ghost" className="ta-btn-sm" onClick={() => { setLoading(true); setLoadError(null); load().then(ok => { if (aliveRef.current) { if (!ok) setLoadError('Impossible de charger les factures.'); setLoading(false); } }); }}>
            Réessayer
          </Btn>
        </div>
      )}

      {!loading && (
        <>
          {toDeclare && declarePeriod && (
            <>
              <div className="eyebrow" style={{ marginBottom: 16 }}>§ 05.A · Déclaration en cours</div>
              <PeriodPanel
                quarter={toDeclare}
                period={declarePeriod}
                tone="warn"
                badge={<Pill tone="warn">à déclarer</Pill>}
                footnote={basisNote}
                cta={
                  <a className="ta-btn ta-btn-primary" href={URSSAF_URL} target="_blank" rel="noreferrer">
                    <span>Déclarer {eur0(declarePeriod.charges.base)} € et payer {eur0(declarePeriod.charges.total)} €</span>
                  </a>
                }
              />
            </>
          )}

          <div className="eyebrow" style={{ margin: '40px 0 16px' }}>
            § 05.{toDeclare ? 'B' : 'A'} · Trimestre en cours
          </div>
          <PeriodPanel
            quarter={current}
            period={currentPeriod}
            tone="accent"
            badge={<Pill tone="accent">en cours</Pill>}
            footnote={
              <div className="ta-callout ta-callout-accent" style={{ marginTop: 24, marginBottom: 0 }}>
                <span>
                  Provision conseillée : mettre de côté <strong>{eur0(currentPeriod.charges.total)} €</strong> pour la
                  déclaration du {fmtDay(current.due)}. Le montant inclut les factures déjà planifiées sur la fin
                  du trimestre.
                  {!toDeclare && ' Aucune déclaration n’est ouverte aujourd’hui.'}
                </span>
              </div>
            }
          />

          <div className="eyebrow" style={{ margin: '48px 0 16px' }}>§ 05.{toDeclare ? 'C' : 'B'} · Historique</div>
          <div className="ta-crow ta-crow-head">
            <div>Trimestre</div>
            <div>Période</div>
            <div className="num">CA</div>
            <div className="num">Charges</div>
            <div className="num">Net</div>
            <div className="num">Statut</div>
          </div>

          {history.length === 0 && (
            <div className="mono" style={{ color: 'var(--ink-3)', padding: '18px 0' }}>
              Aucune facture enregistrée
            </div>
          )}

          {history.map(({ quarter, period }) => {
            const isCurrent = quarter.id === current.id;
            const isToDeclare = toDeclare && quarter.id === toDeclare.id;
            const isOpen = expanded === quarter.id;
            return (
              <div key={quarter.id}>
                <div
                  className={`ta-crow ${isOpen ? 'is-open' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => setExpanded(isOpen ? null : quarter.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpanded(isOpen ? null : quarter.id);
                    }
                  }}
                  style={isCurrent || isToDeclare ? { background: 'var(--bg-1)' } : undefined}
                >
                  <div className="mono" style={{ color: isToDeclare ? 'var(--warn)' : isCurrent ? 'var(--accent)' : 'var(--ink-2)', letterSpacing: '0.06em' }}>
                    {quarter.short}
                  </div>
                  <div className="mono period">
                    {fmtDayShort(quarter.start)} → {fmtDayShort(quarter.end)}
                  </div>
                  <div className="mono tnum num"><i>CA</i>{eur0(period.charges.base)} €</div>
                  <div className="mono tnum num" style={{ color: 'var(--warn)' }}><i>Charges</i>{eur0(period.charges.total)} €</div>
                  <div className="mono tnum num" style={{ color: 'var(--accent)' }}><i>Net</i>{eur0(period.charges.net)} €</div>
                  <div className="status">
                    {isToDeclare ? <Pill tone="warn">à déclarer</Pill>
                      : isCurrent ? <Pill tone="accent">en cours</Pill>
                      : <Pill>clôturé</Pill>}
                  </div>
                </div>

                {isOpen && (
                  <div style={{ padding: '24px 0 32px', borderBottom: '1px solid var(--rule)' }}>
                    <div className="ta-charges-cols">
                      <RevenueToDeclare charges={period.charges} />
                      <ChargesBreakdown charges={period.charges} />
                    </div>
                    {period.invoices.length > 0 && (
                      <div style={{ marginTop: 28 }}>
                        <div className="eyebrow" style={{ marginBottom: 12 }}>{'// '}Factures rattachées</div>
                        {period.invoices.map(inv => (
                          <div
                            key={inv.id}
                            style={{
                              display: 'flex', justifyContent: 'space-between', gap: 16,
                              padding: '9px 0', borderBottom: '1px solid var(--rule)',
                              fontSize: 12.5, alignItems: 'baseline',
                            }}
                          >
                            <span className="mono" style={{ color: 'var(--ink-4)', flex: '0 0 52px' }}>#{inv.num}</span>
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {typeof inv.client === 'string' ? inv.client : (inv.client?.Title || '—')}
                            </span>
                            <span className="mono" style={{ fontSize: 10.5, color: hasCashDate(inv) ? 'var(--ink-3)' : 'var(--ink-4)' }}>
                              {fmtDayShort(inv._date)}{hasCashDate(inv) ? '' : ' · presta'}
                            </span>
                            <span className="mono tnum" style={{ flex: '0 0 100px', textAlign: 'right' }}>
                              {fmtEUR(inv._total)} €
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      <style>{`
        .ta-crow {
          display: grid;
          grid-template-columns: 92px 1fr 130px 130px 130px 116px;
          gap: 20px;
          align-items: center;
          width: 100%;
          padding: 14px 0;
          border: 0;
          border-bottom: 1px solid var(--rule);
          background: transparent;
          color: inherit;
          font: inherit;
          text-align: left;
          cursor: pointer;
          transition: background 0.12s;
        }
        .ta-crow:hover { background: var(--bg-1); }
        .ta-crow .num,
        .ta-crow .status { text-align: right; }
        .ta-crow .num > i { display: none; }
        .ta-crow .period { font-size: 11px; color: var(--ink-4); }
        .ta-crow-head {
          font-family: var(--font-mono);
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--ink-3);
          border-bottom: 1px solid var(--rule-strong);
          cursor: default;
        }
        .ta-crow-head:hover { background: transparent; }
        .ta-charges-cols {
          display: grid;
          grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
          gap: 48px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .ta-charges-cols { grid-template-columns: 1fr; gap: 32px; }
        }
        @media (max-width: 768px) {
          .ta-crow-head { display: none; }
          .ta-crow {
            grid-template-columns: 1fr auto;
            gap: 6px 16px;
            padding: 16px 0;
            align-items: baseline;
          }
          .ta-crow .period { grid-column: 1 / -1; }
          .ta-crow .num { text-align: left; grid-column: 1; }
          .ta-crow .num > i {
            display: inline-block;
            width: 74px;
            font-style: normal;
            font-size: 10px;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: var(--ink-4);
          }
          .ta-crow .status { grid-column: 2; grid-row: 1; }
        }
      `}</style>
    </PageShell>
  );
};

export default Charges;
